import React, { useState, useEffect } from 'react';
import { fetchWorkOrders, fetchWorkOrder } from './api'; // Adjust the import path as necessary
import './workorderlist.css';
import WorkOrderModal from './workordermodal';
import NewWorkOrderModal from './newworkordermodal';
import PaymentBadges from './PaymentBadges';
import { formatDateDisplay, getTodayEastern, getTomorrowEastern, isTodayEastern, isTomorrowEastern, formatDateToEastern } from '../utils/timezone';

const WorkOrderList = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0); // Bumped to reload the list after a change
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState('All');
  const [selectedDate, setSelectedDate] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('In Process');
  const [selectedBilling, setSelectedBilling] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showNewWorkOrderModal, setShowNewWorkOrderModal] = useState(false);
  const [sortByDatePromised, setSortByDatePromised] = useState(false);
  const [showColors, setShowColors] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Format date without year for mobile view
  const formatDateWithoutYear = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}`;
  };

  const toggleNewWorkOrderModal = () => {
    setShowNewWorkOrderModal(!showNewWorkOrderModal);
  };

  const handleWorkOrderCreated = (newWorkOrder) => {
    setWorkOrders(prev => [...prev, newWorkOrder]);
    setShowNewWorkOrderModal(false);
    refreshWorkOrders();
  };

  // Reload the list in place after a work order is created or changed
  const refreshWorkOrders = () => setRefreshKey(key => key + 1);

  const toggleService = (service) => {
    setSelectedService(service);
  };

  const toggleDate = (date) => {
    setSelectedDate(date);
  };

  const toggleStatus = (status) => {
    setSelectedStatus(status);
  };

  const matchesBilling = (workOrder) => {
    if (selectedBilling === 'paid') return workOrder.mark_as_paid;
    if (selectedBilling === 'invoiced') return workOrder.is_invoiced && !workOrder.mark_as_paid;
    if (selectedBilling === 'not_invoiced') return !workOrder.is_invoiced && !workOrder.mark_as_paid;
    return true;
  };

  const toggleSortByDatePromised = () => {
    setSortByDatePromised(!sortByDatePromised);
  };

  const getRowClassName = (workOrder) => {
    if (!showColors) return '';
    if (!workOrder.date_promised) return '';
    
    if (isTodayEastern(workOrder.date_promised)) {
      return 'due-today';
    }
    
    // Check if overdue by comparing with today's date in Eastern Time
    const today = getTodayEastern();
    const promisedDate = formatDateToEastern(workOrder.date_promised);
    
    if (promisedDate < today) {
      return 'past-due';
    }
    
    return '';
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleWorkOrderClick = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShowModal(true);
  };

  // Listen for custom workOrderSelected event from notification clicks
  useEffect(() => {
    const handleWorkOrderSelected = (event) => {
      const workOrderId = event.detail.workOrderId;
      console.log('Received workOrderSelected event for work order ID:', workOrderId);
      
      // Prevent opening if modal is already open
      if (showModal) {
        console.log('Modal already open, ignoring workOrderSelected event');
        return;
      }
      
      // Clear localStorage to prevent duplicate opening
      localStorage.removeItem('selectedWorkOrderId');
      
      // If work orders are already loaded, try to find the work order
      if (workOrders.length > 0) {
        const workOrder = workOrders.find(wo => wo.id === parseInt(workOrderId));
        if (workOrder) {
          console.log('Found work order in loaded list, opening modal:', workOrder.id);
          setSelectedWorkOrder(workOrder);
          setShowModal(true);
        } else {
          // Fetch the work order directly
          console.log('Work order not in loaded list, fetching directly:', workOrderId);
          fetchWorkOrder(parseInt(workOrderId))
            .then(fetchedWorkOrder => {
              console.log('Fetched work order from event:', fetchedWorkOrder);
              setSelectedWorkOrder(fetchedWorkOrder);
              setShowModal(true);
            })
            .catch(error => {
              console.error('Error fetching work order from event:', error);
            });
        }
      } else {
        // Work orders not loaded yet, fetch the work order directly
        console.log('Work orders not loaded yet, fetching directly:', workOrderId);
        fetchWorkOrder(parseInt(workOrderId))
          .then(fetchedWorkOrder => {
            console.log('Fetched work order from event (no work orders loaded):', fetchedWorkOrder);
            setSelectedWorkOrder(fetchedWorkOrder);
            setShowModal(true);
          })
          .catch(error => {
            console.error('Error fetching work order from event (no work orders loaded):', error);
          });
      }
    };

    // Add event listener
    window.addEventListener('workOrderSelected', handleWorkOrderSelected);

    // Cleanup
    return () => {
      window.removeEventListener('workOrderSelected', handleWorkOrderSelected);
    };
  }, [workOrders, showModal]);

  // Check for work order ID in localStorage on component mount
  useEffect(() => {
    // Only check localStorage after initial load is complete
    if (!initialLoadComplete) return;
    
    const selectedWorkOrderId = localStorage.getItem('selectedWorkOrderId');
    if (selectedWorkOrderId) {
      console.log('Found selectedWorkOrderId in localStorage on mount:', selectedWorkOrderId);
      
      // Clear localStorage immediately to prevent duplicate opening
      localStorage.removeItem('selectedWorkOrderId');
      
      // If work orders are already loaded, try to find the work order
      if (workOrders.length > 0) {
        const workOrder = workOrders.find(wo => wo.id === parseInt(selectedWorkOrderId));
        if (workOrder) {
          console.log('Found work order in loaded list on mount:', workOrder.id);
          setSelectedWorkOrder(workOrder);
          setShowModal(true);
        } else {
          // Fetch the work order directly
          console.log('Work order not in loaded list on mount, fetching directly:', selectedWorkOrderId);
          fetchWorkOrder(parseInt(selectedWorkOrderId))
            .then(fetchedWorkOrder => {
              console.log('Fetched work order from localStorage on mount:', fetchedWorkOrder);
              setSelectedWorkOrder(fetchedWorkOrder);
              setShowModal(true);
            })
            .catch(error => {
              console.error('Error fetching work order from localStorage on mount:', error);
            });
        }
      } else {
        // Work orders not loaded yet, fetch the work order directly
        console.log('Work orders not loaded yet on mount, fetching directly:', selectedWorkOrderId);
        fetchWorkOrder(parseInt(selectedWorkOrderId))
          .then(fetchedWorkOrder => {
            console.log('Fetched work order from localStorage (no work orders loaded) on mount:', fetchedWorkOrder);
            setSelectedWorkOrder(fetchedWorkOrder);
            setShowModal(true);
          })
          .catch(error => {
            console.error('Error fetching work order from localStorage (no work orders loaded) on mount:', error);
          });
      }
    }
  }, [initialLoadComplete, workOrders]);

  const handleWorkOrderUpdate = (updatedWorkOrder) => {
    console.log('=== WORK ORDER LIST - handleWorkOrderUpdate ===');
    console.log('Updated work order:', updatedWorkOrder);
    console.log('Is archived:', updatedWorkOrder.is_archived);
    console.log('Current status filter:', selectedStatus);
    console.log('Updated work order status:', updatedWorkOrder.status);
    
    // If work order is now archived, remove it from the main work orders list
    if (updatedWorkOrder.is_archived) {
      console.log('Removing archived work order from main list');
      setWorkOrders(prev => prev.filter(wo => wo.id !== updatedWorkOrder.id));
    } else {
      console.log('Updating work order in main list');
      
      // Check if the updated work order matches the current status and billing filters
      const matchesCurrentFilter = (selectedStatus === 'all' || updatedWorkOrder.status === selectedStatus)
        && matchesBilling(updatedWorkOrder);
      
      if (matchesCurrentFilter) {
        // Update the work order in the list
        setWorkOrders(prev => prev.map(wo => wo.id === updatedWorkOrder.id ? updatedWorkOrder : wo));
      } else {
        // Remove the work order from the list if it no longer matches the filter
        console.log('Work order no longer matches status filter, removing from list');
        setWorkOrders(prev => prev.filter(wo => wo.id !== updatedWorkOrder.id));
      }
    }
    // Then take the list from the server (changes saved elsewhere, e.g. payments from QuickBooks)
    refreshWorkOrders();
  };


  useEffect(() => {
    // A filter changed again before this request finished: drop its (older) results
    let superseded = false;

    const loadWorkOrders = async () => {
      // Convert "All" to empty string for API call
      const serviceFilter = selectedService === 'All' ? '' : selectedService;
      const dateFilter = selectedDate === 'All' ? '' : selectedDate;
      const fetchedWorkOrders = await fetchWorkOrders(serviceFilter, dateFilter, searchQuery, selectedStatus, false, 1, 1000, selectedBilling);
      if (superseded) return;

      if (fetchedWorkOrders && fetchedWorkOrders.results) {
        setWorkOrders(fetchedWorkOrders.results);
      } else if (Array.isArray(fetchedWorkOrders)) {
        // Handle case where API returns array directly
        setWorkOrders(fetchedWorkOrders);
      } else {
        // Handle case where response doesn't have expected structure
        setWorkOrders([]);
      }
      
      // Mark initial load as complete
      setInitialLoadComplete(true);
    };

    loadWorkOrders();
    return () => { superseded = true; };
  }, [selectedService, selectedDate, searchQuery, selectedStatus, selectedBilling, refreshKey]);

  return (
    <div className='workorderlist'>
      <div className='workorderlistfilters'>
        <button className='workorderlistnewworkorderbutton' onClick={toggleNewWorkOrderModal}>
          <span className='workorderlistbuttontext'>New Work Order</span>
          <span className='workorderlistbuttonplus'>+</span>
        </button>
        <div className='workorderlistfiltersrow'>
          <div className='workorderliststatusfilter'>
            <label className='workorderliststatuslabel'>
              <span className='workorderlistlabeltext'>Status:</span>
              <select className='workorderliststatusselect' value={selectedStatus} onChange={(e) => toggleStatus(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="In Process">In Process</option>
                <option value="Called">Called</option>
                <option value="Picked Up">Picked Up</option>
              </select>
            </label>
          </div>
          <div className='workorderliststatusfilter'>
            <label className='workorderliststatuslabel'>
              <span className='workorderlistlabeltext'>Billing:</span>
              <select className='workorderliststatusselect' value={selectedBilling} onChange={(e) => setSelectedBilling(e.target.value)}>
                <option value="all">All</option>
                <option value="not_invoiced">Not Invoiced</option>
                <option value="invoiced">Invoiced (Unpaid)</option>
                <option value="paid">Paid</option>
              </select>
            </label>
          </div>
          <div className='workorderliststatusfilter'>
            <label className='workorderliststatuslabel'>
              <span className='workorderlistlabeltext'>Service:</span>
              <select className='workorderliststatusselect' value={selectedService} onChange={(e) => toggleService(e.target.value)}>
                <option value="All">All Services</option>
                <option value="Restoration">Restoration</option>
                <option value="Straightening">Straightening</option>
                <option value="Repair Crack">Repair Crack</option>
                <option value="Powder Coating">Powder Coating</option>
                <option value="Mount & Balance">Mount & Balance</option>
                <option value="Other">Other</option>
              </select>
            </label>
          </div>
          <div className='workorderliststatusfilter'>
            <label className='workorderliststatuslabel'>
              <span className='workorderlistlabeltext'>Date:</span>
              <select className='workorderliststatusselect' value={selectedDate} onChange={(e) => toggleDate(e.target.value)}>
                <option value="All">All Dates</option>
                <option value="today">Today</option>
                <option value="tomorrow">Tomorrow</option>
              </select>
            </label>
          </div>

          <button className='workorderlistsortButton' onClick={toggleSortByDatePromised}>
            {sortByDatePromised ? 'Due X' : 'Due'}
          </button>

          <button className='workorderlistsortButton' onClick={() => setShowColors(!showColors)}>
            {showColors ? 'No Colors' : 'Colors'}
          </button>
        </div>
        <label className='workorderliststatuslabel workorderlistsearchlabel'>
          <span className='workorderlistlabeltext'>Search:</span>
          <input
            className='workorderlistsearchbar'
            type="text"
            placeholder="⌕ Search Work Orders"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </label>
      </div>
      <button className='workorderlistmobilebutton' onClick={toggleNewWorkOrderModal}>
        <span className='workorderlistbuttonplus'>+</span>
      </button>
      <div className='workorderlistlabelsparent'>
        <div className='workorderlistlabels'>
          <h5 className='workorderlistlabelsheaders'>
            <span className='workorderlistlabeldesktop'>Customer Name</span>
            <span className='workorderlistlabelmobile'>Customer</span>
          </h5>
          <h5 className='workorderlistlabelsheaders'>
            <span className='workorderlistlabeldesktop'>Vehicle Make</span>
            <span className='workorderlistlabelmobile'>Vehicle</span>
          </h5>
          <h5 className='workorderlistlabelsheaders'>Service</h5>
          <h5 className='workorderlistlabelsheaders'>
            <span className='workorderlistlabeldesktop'>Date Promised</span>
            <span className='workorderlistlabelmobile'>Due</span>
          </h5>
          <h5 className='workorderlistlabelsheaders'>
            <span className='workorderlistlabeldesktop'>Work Order #</span>
            <span className='workorderlistlabelmobile'>WO #</span>
          </h5>
          {/* Column for the Invoiced / Paid badges */}
          <h5 className='workorderlistlabelsheaders' aria-hidden='true'></h5>
        </div>
        <hr className='workorderlistlabelsheadersline'/>
      </div>
      <div className='workorderlistscroll'>
        {workOrders && workOrders.length > 0 ? (
          workOrders
            .slice() // Create a shallow copy of the array
            .sort((a, b) => {
              if (sortByDatePromised) {
                // Sort by datePromised in ascending order
                return new Date(a.date_promised) - new Date(b.date_promised);
              }
              // Default to sorting by created_at in descending order
              return new Date(b.created_at) - new Date(a.created_at);
            })
            .map((order) => (
              <div className={`workorderlistrow ${getRowClassName(order)}`} key={order.id} onClick={() => handleWorkOrderClick(order)}>
                <div className='workorderlistrowdataname'>{order.customer_name}</div>
                <div className='workorderlistrowdatavehicle'>{order.vehicle}</div>
                <div className='workorderlistrowdataservice'>{order.service_requested}</div>
                <div className='workorderlistrowdatadate'>
                  <span className='workorderlistlabeldesktop'>{order.date_promised}</span>
                  <span className='workorderlistlabelmobile'>{formatDateWithoutYear(order.date_promised)}</span>
                </div>
                <div className='workorderlistrowdatadate'>{order.id}</div>
                <div className='workorderlistrowbadges'>
                  <PaymentBadges workOrder={order} />
                </div>
              </div>
            ))
        ) : (
          <p>No work orders found</p>
        )}
      </div>

      <NewWorkOrderModal
        showModal={showNewWorkOrderModal}
        toggleModal={toggleNewWorkOrderModal}
        onWorkOrderCreated={handleWorkOrderCreated}
      />
      <WorkOrderModal 
        showModal={showModal} 
        toggleModal={toggleModal} 
        workOrder={selectedWorkOrder}
        onWorkOrderUpdated={handleWorkOrderUpdate}
      />
    </div>
  );
};

export default WorkOrderList;







